import React, { useEffect, useState } from 'react'
import { ActivityIndicator } from 'react-native'
import styled, { useTheme } from 'styled-components/native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { WebViewMessageEvent } from 'react-native-webview'
import { useDispatch, useSelector } from 'APP/Hooks/redux'

import { getColourScheme } from 'APP/Helpers/AppearanceUtils'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { selectAccessToken } from 'APP/Redux/LoginRedux'
import { selectPatientProfile } from 'APP/Redux/PatientRedux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import I18n from 'APP/I18n'
import Config from 'APP/Config'
import DialogueWebView from 'APP/Components/DialogueWebView'

const LoadingOverlay = styled.View`
  background-color: ${({ theme }) => theme.colors.background};
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  z-index: 111;
  display: flex;
  justify-content: center;
  align-items: center;
`

type WebViewNativeMessage = WebViewMessageEvent['nativeEvent']

const IcbtPortalScreen: React.FC = () => {
  const dispatch = useDispatch()
  const accessToken = useSelector(selectAccessToken)
  const { eligibleServices } = useSelector(selectPatientProfile)
  const theme = useTheme()

  const [uriWithAccessToken, setUriWithAccessToken] = useState<string | undefined>()

  const icbtConfig = Config.MICROAPPS_CONFIG?.icbt
  const hasIcbtGuided = Object.prototype.hasOwnProperty.call(eligibleServices, 'icbt_guided')

  const onEvent = (event: MessageEvent | WebViewNativeMessage) => {
    if (event.data) {
      const eventData = isWeb() ? event.data : JSON.parse(event.data)
      if (
        eventData?.type === 'url_changed' &&
        eventData.url &&
        (event as WebViewNativeMessage)?.url?.startsWith(Config.ECOUCH_APP_URL ?? '')
      ) {
        AsyncStorage.setItem('icbt_iframe_last_url', eventData.url)
      } else if (eventData?.type === 'coach_button_clicked') {
        dispatch(PatientHistoryActions.getOrCreateIcbtEpisodeRequest())
      }
    }
  }

  useEffect(() => {
    const colorScheme = getColourScheme()
    const uriWithLangAndTheme = `${icbtConfig?.url?.[I18n.baseLocale]}?lang=${
      I18n.baseLocale
    }&theme=${Config.BRAND_ID}-${colorScheme}${hasIcbtGuided ? '&features=guided' : ''}`

    AsyncStorage.getItem('icbt_iframe_last_url').then((lastUrl) => {
      let redirectUri = ''
      if (lastUrl && !lastUrl.includes('auth/sso')) {
        redirectUri = `&redirect_uri=${lastUrl}`
      }

      setUriWithAccessToken(`${uriWithLangAndTheme}${redirectUri}#access_token=${accessToken}`)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {uriWithAccessToken ? (
        <DialogueWebView
          uri={uriWithAccessToken}
          incognito={true}
          originWhitelist={['https://', 'about:']}
          onEvent={onEvent}
        />
      ) : (
        <LoadingOverlay>
          <ActivityIndicator testID="ICBT_LOADING_INDICATOR" color={theme.colors.text} />
        </LoadingOverlay>
      )}
    </>
  )
}

export default IcbtPortalScreen
